import { getSupabaseClient } from '@/config/supabase';
import { fetchAllOrganizationIds } from '@/services/organizations';
import type { ManagerProfile } from '@/types';

export const MANAGER_SELECT =
  'id, azure_ad_id, email, display_name, role, is_super_admin, organization_ids';

/**
 * Look up the manager profile linked to a Supabase auth user. Returns null when no
 * profile row exists yet (the "authenticated but not provisioned" case).
 */
export async function fetchManagerBySupabaseUserId(
  userId: string,
): Promise<ManagerProfile | null> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('manager_profiles')
    .select(MANAGER_SELECT)
    .eq('supabase_user_id', userId)
    .maybeSingle();
  if (error) throw error;
  return (data as ManagerProfile | null) ?? null;
}

/**
 * Exchange an Azure AD access token for a manager profile, then install a real
 * Supabase session for the linked user. After this, an Azure-authenticated manager
 * is an ordinary Supabase session (auth.uid() = supabase_user_id), so RLS and
 * realtime work identically to the email/password path — and subsequent page loads
 * resolve via the Supabase session, not MSAL.
 */
export async function exchangeAzureToken(accessToken: string): Promise<ManagerProfile> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.functions.invoke('azure-token-exchange', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (error) throw error;
  const { profile, email, emailOtp } = data as {
    profile: ManagerProfile;
    email: string;
    emailOtp: string;
  };
  const { error: otpError } = await supabase.auth.verifyOtp({
    email,
    token: emailOtp,
    type: 'email',
  });
  if (otpError) throw otpError;
  return profile;
}

/**
 * Super admins see every organization. Expand their org scope to all org ids at
 * resolution time. Note this is a login-time snapshot — it's refreshed in-session
 * after a super admin creates a new org (see OrganizationsPage).
 */
export async function finalizeProfile(profile: ManagerProfile): Promise<ManagerProfile> {
  if (!profile.is_super_admin) return profile;
  const organization_ids = await fetchAllOrganizationIds();
  return { ...profile, organization_ids };
}
